import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
    Divider,
} from '@mui/material';
import { getCourseStoreProducts, registerCourseStoreProduct, lookupAppStoreIAP } from '../../redux/actions/courseStoreProductActions';
import AppStoreStepsModal from './AppStoreStepsModal';

const NAV = '#131F2A';

const selectSx = {
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: NAV },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: NAV, borderWidth: '1.5px' },
};

const labelSx = { '&.Mui-focused': { color: NAV } };

const STATUS_COLORS = {
    SUCCESS:     { bg: '#e6f4ea', color: '#1e7e34', label: 'Exitoso' },
    FAILED:      { bg: '#fdecea', color: '#c0392b', label: 'Fallido' },
    IN_PROGRESS: { bg: '#fff8e1', color: '#b8860b', label: 'En progreso' },
};

// ─── Google Play Card (unchanged) ───────────────────────────────────────────

const GooglePlayCard = ({ record, isRegistering, onRegister }) => {
    const [operation, setOperation] = useState('CREATE');
    const loading = isRegistering === 'GOOGLE_PLAY';
    const statusConfig = record ? STATUS_COLORS[record.status] : null;

    return (
        <Card variant="outlined" sx={{ flex: 1, borderColor: 'rgba(19,31,42,0.12)', borderRadius: '8px' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600} color={NAV}>
                    Google Play
                </Typography>

                {record ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={statusConfig.label}
                                size="small"
                                sx={{ backgroundColor: statusConfig.bg, color: statusConfig.color, fontWeight: 600, fontSize: '11px' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {record.operation}
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {record.product_id}
                        </Typography>
                        {record.registered_at && (
                            <Typography variant="caption" color="text.secondary">
                                {new Date(record.registered_at).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </Typography>
                        )}
                        {record.status === 'FAILED' && record.error_message && (
                            <Typography
                                variant="caption"
                                sx={{ color: '#c0392b', backgroundColor: '#fdecea', borderRadius: '4px', p: '4px 8px', display: 'block', mt: 0.5 }}
                            >
                                {record.error_message}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        Sin registros previos
                    </Typography>
                )}

                <Divider sx={{ borderColor: 'rgba(19,31,42,0.08)' }} />

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={labelSx}>Operación</InputLabel>
                        <Select
                            value={operation}
                            label="Operación"
                            onChange={(e) => setOperation(e.target.value)}
                            sx={selectSx}
                        >
                            <MenuItem value="CREATE">CREATE</MenuItem>
                            <MenuItem value="UPDATE">UPDATE</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={loading}
                        onClick={() => onRegister('GOOGLE_PLAY', operation)}
                        sx={{
                            backgroundColor: NAV,
                            boxShadow: 'none',
                            minWidth: 110,
                            '&:hover': { backgroundColor: '#1e2f3e', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: 'rgba(19,31,42,0.4)', color: '#fff' },
                        }}
                    >
                        {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Registrar'}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

// ─── App Store Card ──────────────────────────────────────────────────────────

const AppStoreCard = ({ record, courseId, courseCode, isLookingUp, onLookup }) => {
    const [modalOpen, setModalOpen] = useState(false);

    const steps = record?.app_store_steps || [];
    const successCount = steps.filter((s) => s.status === 'SUCCESS').length;
    const totalSteps = 6;

    let overallStatus = null;
    if (record) {
        const hasFailed = steps.some((s) => s.status === 'FAILED');
        if (successCount === totalSteps) overallStatus = 'SUCCESS';
        else if (hasFailed) overallStatus = 'FAILED';
        else if (successCount > 0) overallStatus = 'IN_PROGRESS';
    }
    const statusConfig = overallStatus ? STATUS_COLORS[overallStatus] : null;
    const storeRef = record?.store_ref || null;

    return (
        <>
            <Card variant="outlined" sx={{ flex: 1, borderColor: 'rgba(19,31,42,0.12)', borderRadius: '8px' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600} color={NAV}>
                        App Store
                    </Typography>

                    {record ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {statusConfig && (
                                    <Chip
                                        label={statusConfig.label}
                                        size="small"
                                        sx={{ backgroundColor: statusConfig.bg, color: statusConfig.color, fontWeight: 600, fontSize: '11px' }}
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {successCount}/{totalSteps} pasos exitosos
                                </Typography>
                            </Box>
                            {storeRef && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    ref: {storeRef}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Sin registros previos
                        </Typography>
                    )}

                    <Divider sx={{ borderColor: 'rgba(19,31,42,0.08)' }} />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {!storeRef && (
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={isLookingUp || !courseCode}
                                onClick={onLookup}
                                sx={{
                                    borderColor: NAV,
                                    color: NAV,
                                    boxShadow: 'none',
                                    minWidth: 130,
                                    '&:hover': { backgroundColor: 'rgba(19,31,42,0.06)', borderColor: NAV },
                                    '&.Mui-disabled': { borderColor: 'rgba(19,31,42,0.3)', color: 'rgba(19,31,42,0.3)' },
                                }}
                            >
                                {isLookingUp
                                    ? <CircularProgress size={16} sx={{ color: NAV }} />
                                    : 'Buscar en Apple'}
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => setModalOpen(true)}
                            sx={{
                                backgroundColor: NAV,
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: '#1e2f3e', boxShadow: 'none' },
                            }}
                        >
                            Ver pasos →
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <AppStoreStepsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                courseId={courseId}
            />
        </>
    );
};

// ─── Main Panel ──────────────────────────────────────────────────────────────

const CourseStoreProductPanel = ({ courseId, courseCode }) => {
    const dispatch = useDispatch();
    const { records, isFetching, isRegistering, isLookingUp } = useSelector((state) => state.courseStoreProduct);

    useEffect(() => {
        if (courseId) dispatch(getCourseStoreProducts(courseId));
    }, [courseId]);

    const googlePlayRecord = records.find((r) => r.store === 'GOOGLE_PLAY') || null;
    const appStoreRecord   = records.find((r) => r.store === 'APP_STORE')   || null;

    const handleRegister = (store, operation) => {
        dispatch(registerCourseStoreProduct({ course_id: Number(courseId), store, operation }));
    };

    return (
        <Box sx={{ mt: 4, mb: 4 }}>
            <Typography
                variant="h6"
                fontWeight={600}
                fontSize={16}
                color={NAV}
                sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(19,31,42,0.12)' }}
            >
                Registros en Tiendas
            </Typography>

            {isFetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: NAV }} />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <GooglePlayCard
                        record={googlePlayRecord}
                        isRegistering={isRegistering}
                        onRegister={handleRegister}
                    />
                    <AppStoreCard
                        record={appStoreRecord}
                        courseId={courseId}
                        courseCode={courseCode}
                        isLookingUp={isLookingUp}
                        onLookup={() => dispatch(lookupAppStoreIAP(courseId))}
                    />
                </Box>
            )}
        </Box>
    );
};

export default CourseStoreProductPanel;
